import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { Bell, AlertTriangle, X, Phone, Mail } from 'lucide-react';
import config from '../../../config';

// npm install socket.io-client

async function apiRequest(path, token, options = {}) {
  const res = await fetch(`${config.baseURL}/api${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || body.message || `Request failed (${res.status})`);
  }
  if (res.status === 204) return null;
  const json = await res.json();
  return json.data;
}

function timeAgo(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const SEVERITY_COLOR = {
  critical: '#c0392b',
  high: '#d97706',
  low: '#2563eb',
};

export default function AlertsBell({ token }) {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null); // NEW — drives the detail popup
  const socketRef = useRef(null);
  const panelRef = useRef(null);

  const loadAlerts = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiRequest('/doctor-portal/alerts', token),
      apiRequest('/doctor-portal/alerts/unread-count', token),
    ])
      .then(([alertRows, count]) => {
        setAlerts(alertRows);
        setUnreadCount(count.count);
      })
      .catch((err) => console.error('Failed to load alerts:', err.message))
      .finally(() => setLoading(false));
  }, [token]);

  // Initial load
  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  // Realtime — connect once, keep the same socket for the dashboard session.
  useEffect(() => {
    if (!token) return;

    const socket = io(config.baseURL, { auth: { token } });
    socketRef.current = socket;

    // socket.on('vitals-alert', (alert) => {
    //   setAlerts((prev) => [alert, ...prev].slice(0, 50));
    //   setUnreadCount((prev) => prev + 1);
    // });
    socket.on('vitals-alert', (alert) => {
      setAlerts((prev) => {
        // Remove any existing alert for this specific patient
        const filtered = prev.filter((a) => a.patient_id !== alert.patient_id);
        // Add the new alert at the very top
        return [alert, ...filtered].slice(0, 50);
      });
      
      // Only increment the badge count if the previous alert for this patient was already read
      // (to prevent the badge from inflating with duplicates)
      setUnreadCount((prev) => prev + 1);
    });
    socket.on('connect_error', (err) => {
      console.warn('Realtime alerts socket failed to connect:', err.message);
    });

    return () => socket.disconnect();
  }, [token]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function handleOpen() {
    setOpen((prev) => !prev);
  }

  async function handleMarkAllRead() {
    try {
      await apiRequest('/doctor-portal/alerts/read-all', token, { method: 'PUT' });
      setUnreadCount(0);
      setAlerts((prev) => prev.map((a) => ({ ...a, is_read: 1 })));
    } catch (err) {
      console.error('Failed to mark all read:', err.message);
    }
  }

  async function handleMarkOneRead(id) {
    try {
      await apiRequest(`/doctor-portal/alerts/${id}/read`, token, { method: 'PUT' });
      setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, is_read: 1 } : a)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark alert read:', err.message);
    }
  }

  return (
    <div style={{ position: 'relative' }} ref={panelRef}>
      <button
        onClick={handleOpen}
        aria-label="Vitals alerts"
        style={{
          position: 'relative',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          padding: 8,
          borderRadius: 8,
        }}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              background: '#c0392b',
              color: 'white',
              borderRadius: 10,
              fontSize: 11,
              fontWeight: 600,
              minWidth: 16,
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            width: 340,
            maxHeight: 420,
            overflowY: 'auto',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 50,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 14px',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <strong style={{ fontSize: 14 }}>Vitals alerts</strong>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  style={{ fontSize: 12, border: 'none', background: 'none', color: '#0f4c75', cursor: 'pointer' }}
                >
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                <X size={14} />
              </button>
            </div>
          </div>

          {loading ? (
            <p style={{ padding: 14, fontSize: 13, color: '#666' }}>Loading…</p>
          ) : alerts.length === 0 ? (
            <p style={{ padding: 14, fontSize: 13, color: '#666' }}>No alerts yet.</p>
          ) : (
            alerts.map((a) => (
              <div
                key={a.id}
                onClick={() => !a.is_read && handleMarkOneRead(a.id)}
                style={{
                  display: 'flex',
                  gap: 10,
                  padding: '10px 14px',
                  borderBottom: '1px solid #f5f5f5',
                  background: a.is_read ? 'white' : '#fafbff',
                  cursor: a.is_read ? 'default' : 'pointer',
                }}
              >
                <AlertTriangle size={16} color={SEVERITY_COLOR[a.severity] || '#666'} style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <p
                    onClick={(e) => {
                      e.stopPropagation(); // don't also trigger the row's mark-read handler
                      setSelectedAlert(a);
                    }}
                    style={{
                      margin: 0,
                      fontSize: 13,
                      fontWeight: a.is_read ? 400 : 600,
                      color: '#0f4c75',
                      textDecoration: 'underline',
                      textDecorationColor: 'transparent',
                      cursor: 'pointer',
                      display: 'inline-block',
                    }}
                  >
                    {a.patient_name}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 12.5, color: '#444' }}>{a.message}</p>
                  <p style={{ margin: '3px 0 0', fontSize: 11, color: '#999' }}>{timeAgo(a.created_at)}</p>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {selectedAlert && (
        <AlertDetailModal alert={selectedAlert} onClose={() => setSelectedAlert(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail popup — name, phone, email, and the alert message.
// ---------------------------------------------------------------------------

function AlertDetailModal({ alert, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 360,
          maxWidth: '90vw',
          boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 16px',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <strong style={{ fontSize: 15 }}>Patient details</strong>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: '16px' }}>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{alert.patient_name}</p>

          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Phone size={14} color="#666" />
              <span style={{ fontSize: 13.5 }}>{alert.patient_phone || 'Not on file'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Mail size={14} color="#666" />
              <span style={{ fontSize: 13.5 }}>{alert.patient_email || 'Not on file'}</span>
            </div>
          </div>

          <div
            style={{
              marginTop: 14,
              padding: '10px 12px',
              borderRadius: 8,
              background: '#fafbff',
              border: '1px solid #eef0f5',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={14} color={SEVERITY_COLOR[alert.severity] || '#666'} />
              <span style={{ fontSize: 12, fontWeight: 600, color: SEVERITY_COLOR[alert.severity] || '#666', textTransform: 'uppercase' }}>
                {alert.severity}
              </span>
            </div>
            <p style={{ margin: '6px 0 0', fontSize: 13.5 }}>{alert.message}</p>
            <p style={{ margin: '6px 0 0', fontSize: 11, color: '#999' }}>{timeAgo(alert.created_at)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}





// import React, { useState, useEffect, useRef, useCallback } from 'react';
// import { io } from 'socket.io-client';
// import { Bell, AlertTriangle, X, Phone, Mail } from 'lucide-react';
// import config from '../../../config';

// // npm install socket.io-client

// async function apiRequest(path, token, options = {}) {
//   const res = await fetch(`${config.baseURL}/api${path}`, {
//     ...options,
//     headers: {
//       'Content-Type': 'application/json',
//       Authorization: `Bearer ${token}`,
//       ...options.headers,
//     },
//   });
//   if (!res.ok) {
//     const body = await res.json().catch(() => ({}));
//     throw new Error(body.error || body.message || `Request failed (${res.status})`);
//   }
//   if (res.status === 204) return null;
//   const json = await res.json();
//   return json.data;
// }

// function timeAgo(iso) {
//   const diffMs = Date.now() - new Date(iso).getTime();
//   const mins = Math.floor(diffMs / 60000);
//   if (mins < 1) return 'just now';
//   if (mins < 60) return `${mins}m ago`;
//   const hours = Math.floor(mins / 60);
//   if (hours < 24) return `${hours}h ago`;
//   return `${Math.floor(hours / 24)}d ago`;
// }

// const SEVERITY_COLOR = {
//   critical: '#c0392b',
//   high: '#d97706',
//   low: '#2563eb',
// };

// export default function AlertsBell({ token }) {
//   const [open, setOpen] = useState(false);
//   const [alerts, setAlerts] = useState([]);
//   const [unreadCount, setUnreadCount] = useState(0);
//   const [loading, setLoading] = useState(true);
//   const [selectedAlert, setSelectedAlert] = useState(null); // NEW — drives the detail popup
//   const socketRef = useRef(null);
//   const panelRef = useRef(null);

//   const loadAlerts = useCallback(() => {
//     setLoading(true);
//     Promise.all([
//       apiRequest('/doctor-portal/alerts', token),
//       apiRequest('/doctor-portal/alerts/unread-count', token),
//     ])
//       .then(([alertRows, count]) => {
//         setAlerts(alertRows);
//         setUnreadCount(count.count);
//       })
//       .catch((err) => console.error('Failed to load alerts:', err.message))
//       .finally(() => setLoading(false));
//   }, [token]);

//   // Initial load
//   useEffect(() => {
//     loadAlerts();
//   }, [loadAlerts]);

//   // Realtime — connect once, keep the same socket for the dashboard session.
//   useEffect(() => {
//     if (!token) return;

//     const socket = io(config.baseURL, { auth: { token } });
//     socketRef.current = socket;

//     socket.on('vitals-alert', (alert) => {
//       setAlerts((prev) => [alert, ...prev].slice(0, 50));
//       setUnreadCount((prev) => prev + 1);
//     });

//     socket.on('connect_error', (err) => {
//       console.warn('Realtime alerts socket failed to connect:', err.message);
//     });

//     return () => socket.disconnect();
//   }, [token]);

//   // Close dropdown on outside click
//   useEffect(() => {
//     function handleClick(e) {
//       if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
//     }
//     document.addEventListener('mousedown', handleClick);
//     return () => document.removeEventListener('mousedown', handleClick);
//   }, []);

//   async function handleOpen() {
//     setOpen((prev) => !prev);
//   }

//   async function handleMarkAllRead() {
//     try {
//       await apiRequest('/doctor-portal/alerts/read-all', token, { method: 'PUT' });
//       setUnreadCount(0);
//       setAlerts((prev) => prev.map((a) => ({ ...a, is_read: 1 })));
//     } catch (err) {
//       console.error('Failed to mark all read:', err.message);
//     }
//   }

//   async function handleMarkOneRead(id) {
//     try {
//       await apiRequest(`/doctor-portal/alerts/${id}/read`, token, { method: 'PUT' });
//       setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, is_read: 1 } : a)));
//       setUnreadCount((prev) => Math.max(0, prev - 1));
//     } catch (err) {
//       console.error('Failed to mark alert read:', err.message);
//     }
//   }

//   return (
//     <div style={{ position: 'relative' }} ref={panelRef}>
//       <button
//         onClick={handleOpen}
//         aria-label="Vitals alerts"
//         style={{
//           position: 'relative',
//           border: 'none',
//           background: 'transparent',
//           cursor: 'pointer',
//           padding: 8,
//           borderRadius: 8,
//         }}
//       >
//         <Bell size={20} />
//         {unreadCount > 0 && (
//           <span
//             style={{
//               position: 'absolute',
//               top: 2,
//               right: 2,
//               background: '#c0392b',
//               color: 'white',
//               borderRadius: 10,
//               fontSize: 11,
//               fontWeight: 600,
//               minWidth: 16,
//               height: 16,
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//               padding: '0 4px',
//             }}
//           >
//             {unreadCount > 99 ? '99+' : unreadCount}
//           </span>
//         )}
//       </button>

//       {open && (
//         <div
//           style={{
//             position: 'absolute',
//             right: 0,
//             top: 'calc(100% + 8px)',
//             width: 340,
//             maxHeight: 420,
//             overflowY: 'auto',
//             background: 'white',
//             border: '1px solid #e5e7eb',
//             borderRadius: 10,
//             boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
//             zIndex: 50,
//           }}
//         >
//           <div
//             style={{
//               display: 'flex',
//               justifyContent: 'space-between',
//               alignItems: 'center',
//               padding: '10px 14px',
//               borderBottom: '1px solid #f0f0f0',
//             }}
//           >
//             <strong style={{ fontSize: 14 }}>Vitals alerts</strong>
//             <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
//               {unreadCount > 0 && (
//                 <button
//                   onClick={handleMarkAllRead}
//                   style={{ fontSize: 12, border: 'none', background: 'none', color: '#0f4c75', cursor: 'pointer' }}
//                 >
//                   Mark all read
//                 </button>
//               )}
//               <button onClick={() => setOpen(false)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
//                 <X size={14} />
//               </button>
//             </div>
//           </div>

//           {loading ? (
//             <p style={{ padding: 14, fontSize: 13, color: '#666' }}>Loading…</p>
//           ) : alerts.length === 0 ? (
//             <p style={{ padding: 14, fontSize: 13, color: '#666' }}>No alerts yet.</p>
//           ) : (
//             alerts.map((a) => (
//               <div
//                 key={a.id}
//                 onClick={() => !a.is_read && handleMarkOneRead(a.id)}
//                 style={{
//                   display: 'flex',
//                   gap: 10,
//                   padding: '10px 14px',
//                   borderBottom: '1px solid #f5f5f5',
//                   background: a.is_read ? 'white' : '#fafbff',
//                   cursor: a.is_read ? 'default' : 'pointer',
//                 }}
//               >
//                 <AlertTriangle size={16} color={SEVERITY_COLOR[a.severity] || '#666'} style={{ flexShrink: 0, marginTop: 2 }} />
//                 <div style={{ flex: 1 }}>
//                   <p
//                     onClick={(e) => {
//                       e.stopPropagation(); // don't also trigger the row's mark-read handler
//                       setSelectedAlert(a);
//                     }}
//                     style={{
//                       margin: 0,
//                       fontSize: 13,
//                       fontWeight: a.is_read ? 400 : 600,
//                       color: '#0f4c75',
//                       textDecoration: 'underline',
//                       textDecorationColor: 'transparent',
//                       cursor: 'pointer',
//                       display: 'inline-block',
//                     }}
//                   >
//                     {a.patient_name}
//                   </p>
//                   <p style={{ margin: '2px 0 0', fontSize: 12.5, color: '#444' }}>{a.message}</p>
//                   <p style={{ margin: '3px 0 0', fontSize: 11, color: '#999' }}>{timeAgo(a.created_at)}</p>
//                 </div>
//               </div>
//             ))
//           )}
//         </div>
//       )}

//       {selectedAlert && (
//         <AlertDetailModal alert={selectedAlert} onClose={() => setSelectedAlert(null)} />
//       )}
//     </div>
//   );
// }

// // ---------------------------------------------------------------------------
// // Detail popup — name, phone, email, and the alert message.
// // ---------------------------------------------------------------------------

// function AlertDetailModal({ alert, onClose }) {
//   return (
//     <div
//       onClick={onClose}
//       style={{
//         position: 'fixed',
//         inset: 0,
//         background: 'rgba(0,0,0,0.35)',
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: 'center',
//         zIndex: 100,
//       }}
//     >
//       <div
//         onClick={(e) => e.stopPropagation()}
//         style={{
//           background: 'white',
//           borderRadius: 12,
//           width: 360,
//           maxWidth: '90vw',
//           boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
//           overflow: 'hidden',
//         }}
//       >
//         <div
//           style={{
//             display: 'flex',
//             justifyContent: 'space-between',
//             alignItems: 'center',
//             padding: '14px 16px',
//             borderBottom: '1px solid #f0f0f0',
//           }}
//         >
//           <strong style={{ fontSize: 15 }}>Patient details</strong>
//           <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
//             <X size={16} />
//           </button>
//         </div>

//         <div style={{ padding: '16px' }}>
//           <p style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{alert.patient_name}</p>

//           <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
//             <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
//               <Phone size={14} color="#666" />
//               <span style={{ fontSize: 13.5 }}>{alert.patient_phone || 'Not on file'}</span>
//             </div>
//             <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
//               <Mail size={14} color="#666" />
//               <span style={{ fontSize: 13.5 }}>{alert.patient_email || 'Not on file'}</span>
//             </div>
//           </div>

//           <div
//             style={{
//               marginTop: 14,
//               padding: '10px 12px',
//               borderRadius: 8,
//               background: '#fafbff',
//               border: '1px solid #eef0f5',
//             }}
//           >
//             <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
//               <AlertTriangle size={14} color={SEVERITY_COLOR[alert.severity] || '#666'} />
//               <span style={{ fontSize: 12, fontWeight: 600, color: SEVERITY_COLOR[alert.severity] || '#666', textTransform: 'uppercase' }}>
//                 {alert.severity}
//               </span>
//             </div>
//             <p style={{ margin: '6px 0 0', fontSize: 13.5 }}>{alert.message}</p>
//             <p style={{ margin: '6px 0 0', fontSize: 11, color: '#999' }}>{timeAgo(alert.created_at)}</p>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }